import React, { useState, useEffect, useRef } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, ComposedChart
} from 'recharts';
import { 
  Building2, Users, FileText, Wallet, CheckCircle2, 
  AlertCircle, TrendingUp, DollarSign, Briefcase, Activity, Settings, X, Eye, EyeOff,
  BookOpen, ArrowUpRight, ArrowDownLeft, Target, Percent, BarChart3, AlertTriangle, AlertOctagon, Star, Plus, FileText as FileTextIcon, GripVertical, ChevronDown
} from 'lucide-react';
import { cn } from '../lib/utils';
import { formatCurrency as formatOrganizationCurrency, getDefaultCurrency } from '../lib/currency';
import { Reorder } from 'motion/react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { db, collection, query, where, onSnapshot, handleFirestoreError, OperationType, doc, setDoc, serverTimestamp } from '../firebase';
import { dashboardDemoData, LOCAL_DEV_ORG_ID } from './dashboardDemoData';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const ResponsiveGridLayout = WidthProvider(Responsive);
const PIE_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

const getLocalAuthToken = () =>
    typeof window === 'undefined' ? null : window.localStorage.getItem('app_auth_token');

const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const token = getLocalAuthToken();
    if (!token) throw new Error('Local session token is unavailable');
    const response = await fetch(path, {
        ...init,
        headers: {
            Authorization: `Bearer ${token}`,
            ...(init.body ? { 'Content-Type': 'application/json' } : {}),
            ...init.headers,
        },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
    return payload as T;
};

const localRecords = <T,>(payload: any): T[] =>
    Array.isArray(payload) ? payload : payload?.documents || payload?.contacts || payload?.tasks ||
    payload?.accounts || payload?.chartOfAccounts || payload?.data || [];

const WidgetWrapper = React.forwardRef<HTMLDivElement, {
    id: string, 
    isVisible: boolean, 
    children: React.ReactNode, 
    className?: string, 
    style?: React.CSSProperties,
    isEditingLayout: boolean,
    toggleLayoutOption: (id: string) => void
} & React.HTMLAttributes<HTMLDivElement>>(({ 
    id, isVisible, children, className, style, isEditingLayout, toggleLayoutOption, ...rest
}, ref) => {
    return (
        <div
            ref={ref}
            id={id}
            style={style}
            {...rest}
            className={cn(
                className,
                "h-full relative group/widget transition-all duration-300",
                isEditingLayout && "ring-2 ring-primary/20",
                !isVisible && isEditingLayout && "opacity-40 grayscale",
                !isVisible && !isEditingLayout && "hidden"
            )}
        >
            {isEditingLayout && (
                <div className="absolute top-4 right-4 z-[60] flex items-center gap-2 animate-in fade-in zoom-in duration-200">
                    <button 
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleLayoutOption(id);
                        }}
                        className={cn(
                            "flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-lg transition-all text-[10px] font-bold uppercase tracking-widest",
                            isVisible 
                                ? "bg-white text-primary border-primary hover:bg-primary/5" 
                                : "bg-red-500 text-white border-red-600 hover:bg-red-600 shadow-[0_0_15px_rgba(239,68,68,0.3)]"
                        )}
                    >
                        {isVisible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        {isVisible ? 'Visible' : 'Hidden'}
                    </button>
                    <div className="p-1.5 bg-card border border-border rounded-full shadow-sm cursor-grab active:cursor-grabbing hover:bg-muted transition-colors drag-handle">
                        <GripVertical className="w-4 h-4 text-muted-foreground pointer-events-none" />
                    </div>
                </div>
            )}
            <div className="h-full">
                {children}
            </div>
        </div>
    );
});

export const ExecutiveDashboard = ({ orgId, orgSettings, onNavigate }: { orgId?: string, orgSettings?: any, onNavigate?: (tab: string) => void }) => {
    const isLocalDev = orgId === LOCAL_DEV_ORG_ID && !isLocalSession();

    // Real state for dashboard updates (seeded with demo data so the local-dev
    // view renders richly without awaiting Firestore).
    const [data, setData] = useState(isLocalDev ? {
        financials: dashboardDemoData.financials,
        aging: dashboardDemoData.aging,
        operations: dashboardDemoData.operations,
        analytics: dashboardDemoData.analytics,
    } : {
        financials: { totalRevenue: 0, lastMonthRevenue: 0, totalBills: 0, lastMonthBills: 0 },
        aging: { 
            receivables: 0, 
            payables: 0,
            pendingInvoicesList: [] as any[],
            pendingBillsList: [] as any[]
        },
        operations: {
            recentContacts: [] as any[],
            activeSessions: 0,
            tasks: { Pending: 0, 'In Progress': 0, Completed: 0 },
            alerts: [] as any[]
        },
        analytics: {
            topCustomers: [] as any[],
            plData: [] as any[],
            cashFlowData: [] as any[],
            expenseBreakdown: [] as any[],
            ratios: {
                currentRatio: 0,
                quickRatio: 0,
                debtToEquity: 0,
                grossMargin: 0,
                netMargin: 0,
                dso: 0,
                dpo: 0,
            },
            budgetVariance: [] as any[],
            ebitdaWaterfall: [] as any[]
        }
    });

    // Local sessions use the PostgreSQL APIs directly; the Firestore listeners below
    // remain available for deployments that still use Firebase.
    useEffect(() => {
        if (!orgId || !isLocalSession()) return;

        let cancelled = false;
        const loadLocalDashboard = async () => {
            try {
                const now = new Date();
                const firstOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                const firstOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
                const [documentsPayload, contactsPayload, tasksPayload, accountsPayload, ledgerPayload, cashFlowPayload] = await Promise.all([
                    localApiRequest(`/api/organizations/${orgId}/documents?types=invoice,bill`),
                    localApiRequest(`/api/organizations/${orgId}/contacts`),
                    localApiRequest(`/api/organizations/${orgId}/tasks`),
                    localApiRequest(`/api/organizations/${orgId}/chart-of-accounts`),
                    localApiRequest(`/api/organizations/${orgId}/reports/general-ledger?from=${sixMonthsAgo.toISOString().slice(0, 10)}`),
                    localApiRequest(`/api/organizations/${orgId}/reports/cash-flow?from=${sixMonthsAgo.toISOString().slice(0, 10)}&to=${now.toISOString().slice(0, 10)}`),
                ]);
                if (cancelled) return;

                const documents = localRecords<any>(documentsPayload).filter(document => document.status !== 'Void');
                const invoices = documents.filter(document => document.type === 'invoice');
                const bills = documents.filter(document => document.type === 'bill');
                const valueOf = (document: any) => Number(document.totalAmount ?? document.total_amount ?? 0);
                const paidOf = (document: any) => Number(document.paidAmount ?? document.paymentReceived ?? document.paid_amount ?? 0);
                const isOutstanding = (document: any) => !['Paid', 'Draft', 'Void', 'Deleted', 'Cancelled'].includes(document.status);
                const inRange = (document: any, start: Date, end?: Date) => {
                    const value = new Date(document.date).getTime();
                    return Number.isFinite(value) && value >= start.getTime() && (!end || value < end.getTime());
                };
                const outstanding = (document: any) => Math.max(0, valueOf(document) - paidOf(document));

                const totalRevenue = invoices.filter(document => inRange(document, firstOfCurrentMonth)).reduce((sum, document) => sum + valueOf(document), 0);
                const lastMonthRevenue = invoices.filter(document => inRange(document, firstOfLastMonth, firstOfCurrentMonth)).reduce((sum, document) => sum + valueOf(document), 0);
                const totalBills = bills.filter(document => inRange(document, firstOfCurrentMonth)).reduce((sum, document) => sum + valueOf(document), 0);
                const lastMonthBills = bills.filter(document => inRange(document, firstOfLastMonth, firstOfCurrentMonth)).reduce((sum, document) => sum + valueOf(document), 0);
                const openInvoices = invoices.filter(isOutstanding);
                const openBills = bills.filter(isOutstanding);

                const customerMap: Record<string, number> = {};
                invoices.forEach(invoice => {
                    const name = invoice.contactName || 'Unknown Customer';
                    customerMap[name] = (customerMap[name] || 0) + valueOf(invoice);
                });
                const topCustomers = Object.entries(customerMap)
                    .map(([name, value]) => ({ name, value, trend: '+0%' }))
                    .sort((a, b) => b.value - a.value)
                    .slice(0, 5);

                const expenseMap: Record<string, number> = {};
                bills.forEach(bill => (bill.items || []).forEach((item: any) => {
                    const category = item.accountName || item.account || 'General Expense';
                    expenseMap[category] = (expenseMap[category] || 0) + Number(item.total ?? item.lineTotal ?? 0);
                }));
                const expenseBreakdown = Object.entries(expenseMap)
                    .map(([name, value]) => ({ name, value }))
                    .sort((a, b) => b.value - a.value)
                    .slice(0, 5);

                const months = Array.from({ length: 6 }, (_, offset) => new Date(now.getFullYear(), now.getMonth() - (5 - offset), 1));
                const plData = months.map(month => {
                    const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);
                    return {
                        name: month.toLocaleString('default', { month: 'short', year: 'numeric' }),
                        revenue: invoices.filter(document => inRange(document, month, nextMonth)).reduce((sum, document) => sum + valueOf(document), 0),
                        expenses: bills.filter(document => inRange(document, month, nextMonth)).reduce((sum, document) => sum + valueOf(document), 0),
                    };
                });
                const cashFlow = cashFlowPayload as any;
                const cashFlowData = months.map(month => {
                    const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);
                    const rowValue = (row: any) => Math.abs(Number(row.net ?? row.amount ?? 0));
                    const belongsToMonth = (row: any) => {
                        const date = new Date(row.date).getTime();
                        return Number.isFinite(date) && date >= month.getTime() && date < nextMonth.getTime();
                    };
                    const inbound = (cashFlow?.inflows || []).filter(belongsToMonth).reduce((sum: number, row: any) => sum + rowValue(row), 0);
                    const outbound = (cashFlow?.outflows || []).filter(belongsToMonth).reduce((sum: number, row: any) => sum + rowValue(row), 0);
                    return { name: month.toLocaleString('default', { month: 'short', year: 'numeric' }), inbound, outbound, net: inbound - outbound };
                });
                const latest = plData[plData.length - 1] || { revenue: 0, expenses: 0 };

                const contacts = [...localRecords<any>(contactsPayload)]
                    .sort((a, b) => new Date(b.createdAt || b.created_at || 0).getTime() - new Date(a.createdAt || a.created_at || 0).getTime())
                    .slice(0, 4)
                    .map(contact => ({ id: contact.id, name: contact.name || contact.contactName, type: contact.type || 'Customer', time: 'Recent' }));
                const tasks = localRecords<any>(tasksPayload);
                const taskCounts = {
                    Pending: tasks.filter(task => ['pending', 'to do', 'todo'].includes(String(task.status || '').toLowerCase())).length,
                    'In Progress': tasks.filter(task => String(task.status || '').toLowerCase() === 'in progress').length,
                    Completed: tasks.filter(task => ['completed', 'done'].includes(String(task.status || '').toLowerCase())).length,
                };
                const accounts = localRecords<any>(accountsPayload);
                setWatchlistAccounts(accounts.filter(account => account.showOnDashboardWatchlist));
                const balances: Record<string, number> = {};
                localRecords<any>(ledgerPayload).forEach(line => {
                    balances[line.account_id ?? line.accountId] = (balances[line.account_id ?? line.accountId] || 0) + Number(line.debit || 0) - Number(line.credit || 0);
                });
                setMonthlyBalances(balances);

                setData(previous => ({
                    ...previous,
                    financials: { ...previous.financials, totalRevenue, lastMonthRevenue, totalBills, lastMonthBills },
                    aging: {
                        ...previous.aging,
                        receivables: openInvoices.reduce((sum, document) => sum + outstanding(document), 0),
                        payables: openBills.reduce((sum, document) => sum + outstanding(document), 0),
                        pendingInvoicesList: [...openInvoices].sort((a, b) => valueOf(b) - valueOf(a)).slice(0, 5).map(invoice => ({ id: invoice.id, contactName: invoice.contactName || 'Unknown Customer', createdAt: invoice.date || '', dueDate: invoice.dueDate || '', amount: outstanding(invoice) })),
                        pendingBillsList: [...openBills].sort((a, b) => valueOf(b) - valueOf(a)).slice(0, 5).map(bill => ({ id: bill.id, supplierName: bill.contactName || 'Unknown Supplier', createdAt: bill.date || '', dueDate: bill.dueDate || '', amount: outstanding(bill) })),
                    },
                    operations: { ...previous.operations, recentContacts: contacts, tasks: taskCounts, activeSessions: 1 },
                    analytics: {
                        ...previous.analytics,
                        topCustomers,
                        expenseBreakdown,
                        plData,
                        cashFlowData,
                        ebitdaWaterfall: [
                            { name: 'Revenue', value: latest.revenue },
                            { name: 'Opex', value: -latest.expenses },
                            { name: 'EBITDA', value: latest.revenue - latest.expenses, isTotal: true },
                        ],
                    },
                }));
            } catch (error) {
                console.error('Local dashboard data load failed:', error);
            }
        };
        void loadLocalDashboard();
        return () => { cancelled = true; };
    }, [orgId]);

    // Fetch Invoices for Revenue & Receivables
    useEffect(() => {
        if (!orgId || isLocalDev || isLocalSession()) return;

        const q = query(
            collection(db, 'documents'),
            where('orgId', '==', orgId),
            where('type', '==', 'invoice'),
            where('status', '!=', 'Void')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const invoices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));
            
            const now = new Date();
            const firstOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
            const firstOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();

            const totalRevenue = invoices
                .filter(inv => {
                    const dateVal = inv.date?.toDate ? inv.date.toDate() : inv.date;
                    const d = new Date(dateVal).getTime();
                    return d >= firstOfCurrentMonth;
                })
                .reduce((sum, inv) => sum + (Number(inv.totalAmount) || 0), 0);
            
            const lastMonthRevenue = invoices
                .filter(inv => {
                    const dateVal = inv.date?.toDate ? inv.date.toDate() : inv.date;
                    const d = new Date(dateVal).getTime();
                    return d >= firstOfLastMonth && d < firstOfCurrentMonth;
                })
                .reduce((sum, inv) => sum + (Number(inv.totalAmount) || 0), 0);

            const receivables = invoices
                .filter(inv => inv.status !== 'Paid' && inv.status !== 'Draft')
                .reduce((sum, inv) => sum + (Number(inv.totalAmount) || 0), 0);
            
            const pendingInvoicesList = invoices
                .filter(inv => inv.status !== 'Paid' && inv.status !== 'Draft')
                .sort((a, b) => (Number(b.totalAmount) || 0) - (Number(a.totalAmount) || 0))
                .slice(0, 5)
                .map(inv => ({
                    id: inv.id,
                    contactName: inv.contactName || 'Unknown Customer',
                    createdAt: inv.date || '',
                    dueDate: inv.dueDate || '',
                    amount: Number(inv.totalAmount) || 0
                }));

            // Top Customers
            const customerMap: Record<string, number> = {};
            invoices.forEach(inv => {
                const name = inv.contactName || 'Unknown Customer';
                customerMap[name] = (customerMap[name] || 0) + (Number(inv.totalAmount) || 0);
            });
            const topCustomers = Object.entries(customerMap)
                .map(([name, value]) => ({ name, value, trend: '+0%' }))
                .sort((a, b) => b.value - a.value)
                .slice(0, 5);

            setData(prev => ({
                ...prev,
                financials: { ...prev.financials, totalRevenue, lastMonthRevenue },
                aging: { ...prev.aging, receivables, pendingInvoicesList },
                analytics: { ...prev.analytics, topCustomers }
            }));
        }, (error) => handleFirestoreError(error, OperationType.LIST, 'invoices'));

        return () => unsubscribe();
    }, [orgId]);

    // Fetch Bills for Expenses & Payables
    useEffect(() => {
        if (!orgId || isLocalDev || isLocalSession()) return;

        const q = query(
            collection(db, 'documents'),
            where('orgId', '==', orgId),
            where('type', '==', 'bill'),
            where('status', '!=', 'Void')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const bills = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));
            
            const now = new Date();
            const firstOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
            const firstOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();

            const totalBills = bills
                .filter(bill => {
                    const dateVal = bill.date?.toDate ? bill.date.toDate() : bill.date;
                    const d = new Date(dateVal).getTime();
                    return d >= firstOfCurrentMonth;
                })
                .reduce((sum, bill) => sum + (Number(bill.totalAmount) || 0), 0);
            
            const lastMonthBills = bills
                .filter(bill => {
                    const dateVal = bill.date?.toDate ? bill.date.toDate() : bill.date;
                    const d = new Date(dateVal).getTime();
                    return d >= firstOfLastMonth && d < firstOfCurrentMonth;
                })
                .reduce((sum, bill) => sum + (Number(bill.totalAmount) || 0), 0);

            const payables = bills
                .filter(bill => bill.status !== 'Paid' && bill.status !== 'Draft')
                .reduce((sum, bill) => sum + (Number(bill.totalAmount) || 0), 0);
            
            const pendingBillsList = bills
                .filter(bill => bill.status !== 'Paid' && bill.status !== 'Draft')
                .sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0))
                .slice(0, 5)
                .map(bill => ({
                    id: bill.id,
                    supplierName: bill.contactName || 'Unknown Supplier',
                    createdAt: bill.date || '',
                    dueDate: bill.dueDate || '',
                    amount: bill.totalAmount || 0
                }));

            setData(prev => ({
                ...prev,
                financials: { ...prev.financials, totalBills, lastMonthBills },
                aging: { ...prev.aging, payables, pendingBillsList }
            }));
        }, (error) => handleFirestoreError(error, OperationType.LIST, 'documents (bills)'));

        return () => unsubscribe();
    }, [orgId]);

    // Fetch Recent Contacts
    useEffect(() => {
        if (!orgId || isLocalDev || isLocalSession()) return;

        const q = query(
            collection(db, 'contacts'),
            where('orgId', '==', orgId)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const contacts = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() as any }))
                .sort((a, b) => {
                    const dateA = a.createdAt?.toDate?.()?.getTime() || new Date(a.createdAt || 0).getTime();
                    const dateB = b.createdAt?.toDate?.()?.getTime() || new Date(b.createdAt || 0).getTime();
                    return dateB - dateA;
                })
                .slice(0, 4)
                .map(c => ({
                    id: c.id,
                    name: c.name || c.contactName,
                    type: c.type || 'Customer',
                    time: 'Recent'
                }));

            setData(prev => ({
                ...prev,
                operations: { ...prev.operations, recentContacts: contacts }
            }));
        }, (error) => handleFirestoreError(error, OperationType.LIST, 'contacts'));

        return () => unsubscribe();
    }, [orgId]);

    // Fetch Tasks
    useEffect(() => {
        if (!orgId || isLocalDev || isLocalSession()) return;

        const q = query(
            collection(db, 'tasks_mops'),
            where('orgId', '==', orgId)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const tasks = snapshot.docs.map(doc => doc.data() as any);
            const counts = {
                'Pending': tasks.filter(t => t.status === 'Pending' || t.status === 'To Do').length,
                'In Progress': tasks.filter(t => t.status === 'In Progress').length,
                'Completed': tasks.filter(t => t.status === 'Completed' || t.status === 'Done').length
            };

            setData(prev => ({
                ...prev,
                operations: { ...prev.operations, tasks: counts }
            }));
        }, (error) => handleFirestoreError(error, OperationType.LIST, 'tasks_mops'));

        return () => unsubscribe();
    }, [orgId]);

    // Generate P&L and Cash Flow data from Invoices, Bills and Transactions
    useEffect(() => {
        if (!orgId || isLocalDev || isLocalSession()) return;

        const combinedUnsubscribe = () => {
            unsubInv();
            unsubBill();
            unsubTx();
        };

        let currentInvoices: any[] = [];
        let currentBills: any[] = [];
        let currentTransactions: any[] = [];

        const updateAnalytics = () => {
            try {
                // 1. Expense Breakdown (by category/account)
                const categoryMap: Record<string, number> = {};
                currentBills.forEach(bill => {
                    if (!bill) return;
                    (bill.items || []).forEach((item: any) => {
                        if (!item) return;
                        const cat = item.accountName || 'General Expense';
                        categoryMap[cat] = (categoryMap[cat] || 0) + (Number(item.total) || 0);
                    });
                });
                currentTransactions.forEach(tx => {
                    if (!tx) return;
                    if (tx.amount < 0 && !tx.billId) {
                        const cat = tx.category || 'Other Payments';
                        categoryMap[cat] = (categoryMap[cat] || 0) + Math.abs(Number(tx.amount) || 0);
                    }
                });
                const expenseBreakdown = Object.entries(categoryMap)
                    .map(([name, value]) => ({ name, value }))
                    .sort((a, b) => b.value - a.value)
                    .slice(0, 5);

                // 2. Multi-month Analytics (Last 6 Months)
                const plData = [];
                const cashFlowData = [];
                const now = new Date();
                
                for (let i = 5; i >= 0; i--) {
                    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                    const monthLabel = d.toLocaleString('default', { month: 'short', year: 'numeric' });
                    const monthStart = d.getTime();
                    const nextMonth = new Date(d.getFullYear(), d.getMonth() + 1, 1);
                    const monthEnd = nextMonth.getTime();

                    // P&L (Accrual basis)
                    const monthInvoices = currentInvoices.filter(inv => {
                        if (!inv || !inv.date) return false;
                        const invDate = new Date(inv.date).getTime();
                        return invDate >= monthStart && invDate < monthEnd;
                    });
                    const monthBills = currentBills.filter(bill => {
                        if (!bill || !bill.date) return false;
                        const billDate = new Date(bill.date).getTime();
                        return billDate >= monthStart && billDate < monthEnd;
                    });

                    const revenue = monthInvoices.reduce((sum, inv) => sum + (Number(inv.totalAmount) || 0), 0);
                    const expenses = monthBills.reduce((sum, bill) => sum + (Number(bill.totalAmount) || 0), 0);

                    plData.push({ name: monthLabel, revenue, expenses });

                    // Cash Flow (Cash basis)
                    const monthTxs = currentTransactions.filter(tx => {
                        if (!tx || !tx.date) return false;
                        const txDate = new Date(tx.date).getTime();
                        return txDate >= monthStart && txDate < monthEnd;
                    });

                    const inbound = monthTxs.filter(tx => (Number(tx.amount) || 0) > 0).reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
                    const outbound = monthTxs.filter(tx => (Number(tx.amount) || 0) < 0).reduce((sum, tx) => sum + Math.abs(Number(tx.amount) || 0), 0);
                    const net = inbound - outbound;

                    cashFlowData.push({ name: monthLabel, inbound, outbound, net });
                }

                // 3. Simple EBITDA Waterfall simulation for realism
                const latestRevenue = plData.length > 0 ? plData[plData.length - 1].revenue : 0;
                const latestExpenses = plData.length > 0 ? plData[plData.length - 1].expenses : 0;
                const ebitdaWaterfall = [
                    { name: 'Revenue', value: latestRevenue },
                    { name: 'Opex', value: -latestExpenses },
                    { name: 'EBITDA', value: latestRevenue - latestExpenses, isTotal: true },
                ];

                setData(prev => ({
                    ...prev,
                    analytics: {
                        ...prev.analytics,
                        expenseBreakdown,
                        plData,
                        cashFlowData,
                        ebitdaWaterfall
                    }
                }));
            } catch (error) {
                console.error("Error updating dashboard analytics:", error);
            }
        };

        const unsubInv = onSnapshot(query(collection(db, 'documents'), where('orgId', '==', orgId), where('type', '==', 'invoice'), where('status', '!=', 'Void')), (snap) => {
            currentInvoices = snap.docs.map(doc => doc.data());
            updateAnalytics();
        }, (error) => handleFirestoreError(error, OperationType.LIST, 'documents (analytics_invoices)'));
        const unsubBill = onSnapshot(query(collection(db, 'documents'), where('orgId', '==', orgId), where('type', '==', 'bill'), where('status', '!=', 'Void')), (snap) => {
            currentBills = snap.docs.map(doc => doc.data());
            updateAnalytics();
        }, (error) => handleFirestoreError(error, OperationType.LIST, 'documents (analytics_bills)'));
        const unsubTx = onSnapshot(query(collection(db, 'transactions'), where('orgId', '==', orgId), where('status', '==', 'Completed')), (snap) => {
            currentTransactions = snap.docs.map(doc => doc.data());
            updateAnalytics();
        }, (error) => handleFirestoreError(error, OperationType.LIST, 'transactions (analytics)'));

        return combinedUnsubscribe;
    }, [orgId]);



    const [isEditingLayout, setIsEditingLayout] = useState(false);
    const [layout, setLayout] = useState({
        showAlerts: true,
        showRevenueKpi: true,
        showBillsKpi: true,
        showPendingInvoicesKpi: true,
        showPendingBillsKpi: true,
        showPnLGraph: true,        
        showCashFlowGraph: true,
        showExpenseBreakdown: true,
        showTopCustomers: true,
        showTopExpenses: true,
        showPendingInvoicesList: true,
        showPendingBillsList: true,
        showRecentContacts: true,
        showAttendance: true,
        showTasks: true,
        showAccountWatchlist: true,
        showFinancialRatios: true,
        showBudgetVariance: true,
        showEbitdaWaterfall: true
    });
    const DEFAULT_WIDGET_ORDER = [
        'showAlerts', 
        'showRevenueKpi', 'showBillsKpi', 'showPendingInvoicesKpi', 'showPendingBillsKpi',
        'showPnLGraph', 'showCashFlowGraph', 'showExpenseBreakdown', 
        'showTopCustomers', 'showTopExpenses',
        'showPendingInvoicesList', 'showPendingBillsList',
        'showFinancialRatios', 'showBudgetVariance', 'showEbitdaWaterfall',
        'showAccountWatchlist', 'showRecentContacts', 'showAttendance', 'showTasks'
    ];
    const [layoutOrder, setLayoutOrder] = useState<string[]>(DEFAULT_WIDGET_ORDER);

    // Widget sizing categories used to derive responsive layouts.
    // "wide"  -> spans the full available row at each breakpoint
    // "half"  -> two-up on large screens, stacks to full on tiny screens
    // "kpi"   -> compact stat cards (auto-flowed in pairs/groups)
    const WIDGET_WIDTHS: Record<string, { wide?: boolean; half?: boolean; kpi?: boolean }> = {
        showAlerts: { wide: true },
        showRevenueKpi: { kpi: true },
        showBillsKpi: { kpi: true },
        showPendingInvoicesKpi: { kpi: true },
        showPendingBillsKpi: { kpi: true },
        showPnLGraph: { wide: true },
        showCashFlowGraph: { wide: true },
        showExpenseBreakdown: { half: true },
        showFinancialRatios: { half: true },
        showEbitdaWaterfall: { wide: true },
        showBudgetVariance: { wide: true },
        showTopCustomers: { half: true },
        showTopExpenses: { half: true },
        showPendingInvoicesList: { half: true },
        showPendingBillsList: { half: true },
        showAccountWatchlist: { wide: true },
        showRecentContacts: { half: true },
        showAttendance: { half: true },
        showTasks: { half: true }
    };

    const DEFAULT_WIDGET_HEIGHTS: Record<string, number> = {
        showAlerts: 4,
        showRevenueKpi: 2,
        showBillsKpi: 2,
        showPendingInvoicesKpi: 2,
        showPendingBillsKpi: 2,
        showPnLGraph: 5,
        showCashFlowGraph: 5,
        showExpenseBreakdown: 5,
        showFinancialRatios: 5,
        showEbitdaWaterfall: 5,
        showBudgetVariance: 5,
        showTopCustomers: 4,
        showTopExpenses: 4,
        showPendingInvoicesList: 4,
        showPendingBillsList: 4,
        showAccountWatchlist: 4,
        showRecentContacts: 4,
        showAttendance: 4,
        showTasks: 4
    };

    const buildLayout = (cols: number): ReactGridLayout.Layout[] => {
        const result: ReactGridLayout.Layout[] = [];
        let x = 0;
        let y = 0;
        for (const id of DEFAULT_WIDGET_ORDER) {
            const spec = WIDGET_WIDTHS[id];
            const h = DEFAULT_WIDGET_HEIGHTS[id] ?? 4;
            let w: number;
            if (spec?.kpi) {
                // KPI cards: 2-up on sm+, full-width on tiny screens.
                w = cols >= 6 ? 2 : 2;
                if (cols <= 4) w = cols;       // stack full-width on xs/xxs
            } else if (spec?.wide) {
                w = cols;                       // full row
            } else {
                // half: share row with another half; full row on small screens
                w = cols >= 10 ? Math.floor(cols / 2) : cols >= 6 ? 3 : cols;
            }
            // Wrap to next row when we run out of space.
            if (x + w > cols) {
                x = 0;
                y += (spec?.kpi ? 2 : h);
            }
            result.push({ i: id, x, y, w, h });
            x += w;
            // KPIs run in pairs; wide tiles always start a fresh row.
            if (spec?.wide) {
                x = 0;
                y += h;
            }
        }
        return result;
    };

    const DEFAULT_LAYOUTS: ReactGridLayout.Layouts = {
        lg: buildLayout(12),
        md: buildLayout(10),
        sm: buildLayout(6),
        xs: buildLayout(4),
        xxs: buildLayout(2)
    };
    const [layouts, setLayouts] = useState<ReactGridLayout.Layouts>(DEFAULT_LAYOUTS);


    const [watchlistAccounts, setWatchlistAccounts] = useState<any[]>([]);
    const [monthlyBalances, setMonthlyBalances] = useState<Record<string, number>>({});

    // Seed watchlist + monthly balances for local-dev preview.
    useEffect(() => {
        if (!isLocalDev) return;
        setWatchlistAccounts(dashboardDemoData.watchlistAccounts);
        const balances: Record<string, number> = {};
        dashboardDemoData.watchlistAccounts.forEach(acc => {
            balances[acc.id] = acc.nature === 'Debit' ? (acc.ytd / 6) : -(acc.ytd / 6);
        });
        setMonthlyBalances(balances);
    }, [isLocalDev]);

    useEffect(() => {
        if (!orgId || isLocalDev || isLocalSession()) return;

        const q = query(
            collection(db, 'chart_of_accounts'),
            where('orgId', '==', orgId),
            where('showOnDashboardWatchlist', '==', true)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const accs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setWatchlistAccounts(accs);
        });

        return () => unsubscribe();
    }, [orgId]);

    useEffect(() => {
        if (!orgId || isLocalDev || isLocalSession()) return;

        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        
        const q = query(
            collection(db, 'manual_journals'),
            where('orgId', '==', orgId),
            where('status', '==', 'Posted'),
            where('date', '>=', startOfMonth)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const balances: Record<string, number> = {};
            
            snapshot.docs.forEach(doc => {
                const journal = doc.data();
                journal.lines?.forEach((line: any) => {
                    if (!balances[line.accountId]) balances[line.accountId] = 0;
                    balances[line.accountId] += (line.debit || 0) - (line.credit || 0);
                });
            });
            
            setMonthlyBalances(balances);
        });

        return () => unsubscribe();
    }, [orgId]);

    const [hasLoadedPrefs, setHasLoadedPrefs] = useState(false);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Persistence
    useEffect(() => {
        if (!orgId) return;

        if (isLocalSession()) {
            let cancelled = false;
            localApiRequest<any>(`/api/organizations/${orgId}/dashboard-preferences`)
                .then((prefs) => {
                    if (cancelled) return;
                    if (prefs?.layout) setLayout(prefs.layout);
                    if (prefs?.layoutGrid) setLayouts(prefs.layoutGrid);
                    if (prefs?.layoutOrder) setLayoutOrder(prefs.layoutOrder);
                    setHasLoadedPrefs(true);
                })
                .catch((error) => {
                    if (!cancelled) {
                        console.error('Local dashboard preferences load failed:', error);
                        setHasLoadedPrefs(true);
                    }
                });
            return () => { cancelled = true; };
        }

        if (isLocalDev) return;

        const q = query(
            collection(db, 'dashboard_preferences'),
            where('orgId', '==', orgId)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!snapshot.empty) {
                const doc = snapshot.docs[0];
                const prefs = doc.data();
                if (prefs.layout) setLayout(prefs.layout);
                if (prefs.layoutGrid) setLayouts(prefs.layoutGrid);
                if (prefs.layoutOrder) setLayoutOrder(prefs.layoutOrder);
            }
            setHasLoadedPrefs(true);
        }, (error) => handleFirestoreError(error, OperationType.LIST, 'dashboard_preferences'));

        return () => {
            unsubscribe();
            // Do NOT clear timeout here, let it finish the save even if we navigate away
            // This ensures changes are persisted after navigation
        };
    }, [orgId]);

    const savePreferences = (newLayout: any, newLayouts: any, newOrder: string[]) => {
        if (!orgId || !hasLoadedPrefs) return;
        
        // Debounce saves - shortened to 300ms for responsiveness and faster persistence before navigation
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        
        saveTimeoutRef.current = setTimeout(async () => {
            // Helper to recursively remove undefined values which Firestore doesn't support
            const cleanData = (obj: any): any => {
                if (obj === null || obj === undefined) return null; // Convert undefined to null
                if (Array.isArray(obj)) return obj.map(cleanData);
                if (typeof obj === 'object') {
                    const result: any = {};
                    Object.keys(obj).forEach(key => {
                        const value = obj[key];
                        if (value !== undefined) {
                            result[key] = cleanData(value);
                        }
                    });
                    return result;
                }
                return obj;
            };

            // Prepare data object, ensuring no undefined values are passed
            const prefs: any = {
                orgId,
                updatedAt: isLocalSession() ? new Date().toISOString() : serverTimestamp()
            };
            
            if (newLayout !== undefined) prefs.layout = cleanData(newLayout);
            if (newLayouts !== undefined) prefs.layoutGrid = cleanData(newLayouts);
            if (newOrder !== undefined) prefs.layoutOrder = cleanData(newOrder);

            try {
                if (isLocalSession()) {
                    await localApiRequest(`/api/organizations/${orgId}/dashboard-preferences`, {
                        method: 'PUT',
                        body: JSON.stringify(prefs),
                    });
                    console.log('Dashboard preferences saved locally');
                } else {
                    await setDoc(doc(db, 'dashboard_preferences', orgId), prefs, { merge: true });
                    console.log('Dashboard preferences saved to Firestore');
                }
            } catch (error) {
                if (isLocalSession()) console.error('Local dashboard preferences save failed:', error);
                else handleFirestoreError(error, OperationType.WRITE, 'dashboard_preferences');
            }
        }, 1000); // 1 second debounce
    };

    const WIDGET_LABELS: Record<string, string> = {
    showAlerts: "Alerts & Anomalies",
    showRevenueKpi: "Total Revenue KPI",
    showBillsKpi: "Total Bills KPI",
    showPendingInvoicesKpi: "Pending Invoices KPI",
    showPendingBillsKpi: "Pending Bills KPI",
    showPnLGraph: "Profit & Loss Overview",
    showCashFlowGraph: "Cash In/Out Flow",
    showExpenseBreakdown: "Expense Breakdown",
    showTopCustomers: "Top Customers",
    showTopExpenses: "Top Expense Categories",
    showPendingInvoicesList: "Pending Invoices List",
    showPendingBillsList: "Pending Bills List (Suppliers)",
    showFinancialRatios: "Financial Ratios & Metrics",
    showBudgetVariance: "Budget vs Actual Variance",
    showEbitdaWaterfall: "EBITDA Waterfall",
    showAccountWatchlist: "Account Watchlist",
    showRecentContacts: "Recent Contacts",
    showAttendance: "Attendance Center",
    showTasks: "Task Management"
};

    const toggleLayoutOption = (key: string) => {
        const newLayout = { ...layout, [key]: !layout[key as keyof typeof layout] };
        setLayout(newLayout as any);
        savePreferences(newLayout as any, layouts, layoutOrder);
    };

    // No simulation
    useEffect(() => {
        // Just maintain active sessions count for UI feel if needed, but not fluctuating mock data
        setData(prev => ({
            ...prev,
            operations: {
                ...prev.operations,
                activeSessions: Math.floor(Math.random() * 5) + 1
            }
        }));
    }, []);

    const [isQuickActionOpen, setIsQuickActionOpen] = useState(false);
    const quickActionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (quickActionRef.current && !quickActionRef.current.contains(event.target as Node)) {
                setIsQuickActionOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const QUICK_ACTIONS = [
        { label: 'Create Quotation', target: 'quotes', actionId: 'new-quote' },
        { label: 'Create Invoice', target: 'invoices', actionId: 'new-invoice' },
        { label: 'Create Purchase Order', target: 'purchase_orders', actionId: 'new-po' },
        { label: 'Create Bill', target: 'bills', actionId: 'new-bill' },
        { label: 'Create Manual Journal', target: 'manual_journal', actionId: 'new-journal' },
        { label: 'Create Item', target: 'items', actionId: 'new-item' },
        { label: 'Create Contact', target: 'contacts', actionId: 'new-contact' },
        { label: 'Create Task', target: 'tasks_mops', actionId: 'new-task' }
    ];

    const handleQuickAction = (target: string, actionId: string) => {
        setIsQuickActionOpen(false);
        if (onNavigate) {
            onNavigate(target);
        }
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('QUICK_ACTION', { detail: { action: actionId } }));
        }, 100);
    };

    const currency = getDefaultCurrency(orgSettings);
    const formatCurrency = (val: number) => formatOrganizationCurrency(val, currency, { maximumFractionDigits: 0 });
    const formatCompactCurrency = (val: number) => formatOrganizationCurrency(val, currency, {
        notation: 'compact',
        maximumFractionDigits: 1,
    });

    return (
        <div className="flex flex-col gap-6 relative">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between col-span-1 border-b pb-4 md:col-span-12 mb-2 break-inside-avoid editorial-fade relative z-50" style={{ order: -100 }}>
                <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground mb-1">Insights</p>
                    <h2 className="font-display text-2xl sm:text-3xl font-extrabold flex items-center gap-2 tracking-tight truncate">
                        Executive Dashboard
                    </h2>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                    <div className="hidden md:flex items-center gap-2 text-xs font-bold text-muted-foreground bg-muted/40 backdrop-blur-sm px-3 py-1.5 rounded-full border border-border/60">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        Live Data Active
                    </div>
                    
                    <div className="relative" ref={quickActionRef}>
                        <Button
                            type="button"
                            onClick={() => setIsQuickActionOpen(!isQuickActionOpen)}
                            className="rounded-lg"
                        >
                            <Plus className="w-4 h-4" />
                            Quick Action
                            <ChevronDown className="w-4 h-4" />
                        </Button>
                        
                        {isQuickActionOpen && (
                            <div className="absolute right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-xl shadow-xl z-[100] overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                                <div className="p-2 space-y-1">
                                    {QUICK_ACTIONS.map(action => (
                                        <button
                                            key={action.actionId}
                                            onClick={() => handleQuickAction(action.target, action.actionId)}
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted hover:text-primary rounded-lg transition-colors font-semibold flex items-center gap-2"
                                        >
                                            <div className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                                            {action.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    
</div>

                    <Button
                        type="button"
                        variant={isEditingLayout ? 'default' : 'outline'}
                        onClick={() => setIsEditingLayout(!isEditingLayout)}
                        className="rounded-lg"
                    >
                        {isEditingLayout ? (
                            <>
                                <CheckCircle2 className="w-4 h-4" />
                                Done Editing
                            </>
                        ) : (
                            <>
                                <Settings className="w-4 h-4" />
                                Edit Layout
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {isEditingLayout && (
                <div className="fixed bottom-4 left-2 right-2 sm:left-auto sm:right-auto sm:bottom-10 sm:w-auto sm:max-w-[90vw] sm:left-1/2 sm:-translate-x-1/2 z-[100] flex flex-col gap-4 sm:flex-row sm:items-center bg-black/90 backdrop-blur-xl border border-white/10 p-4 sm:px-6 sm:py-4 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] animate-in slide-in-from-bottom-10 fade-in duration-500">
                    <div className="flex items-center gap-4 sm:pr-6 sm:border-r sm:border-white/10">
                        <div className="w-10 h-10 bg-primary/20 rounded-2xl flex items-center justify-center shrink-0">
                            <Settings className="w-5 h-5 text-primary animate-spin-slow" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[10px] uppercase tracking-widest font-black text-white/50">Mode</span>
                            <span className="text-sm font-bold text-white uppercase tracking-tighter">Layout Customization</span>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-4 sm:gap-6">
                        <div className="flex flex-col">
                            <span className="text-[9px] uppercase tracking-widest font-black text-white/40">Instructions</span>
                            <span className="text-[11px] text-white/80 font-medium">Drag widgets • Tap Eye to toggle</span>
                        </div>
                        <button 
                            onClick={() => setIsEditingLayout(false)}
                            className="bg-primary text-primary-foreground px-5 py-2 rounded-lg text-xs font-bold hover:bg-primary/90 transition-colors shrink-0"
                        >
                            Finish
                        </button>
                    </div>
                </div>
            )}


            
            
            <ResponsiveGridLayout
                className="layout"
                measureBeforeMount
                layouts={layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={100}
                onLayoutChange={(currLayout, allLayouts) => {
                    setLayouts(allLayouts as any);
                    savePreferences(layout, allLayouts as any, layoutOrder);
                }}
                isDraggable={isEditingLayout}
                isResizable={isEditingLayout}
                draggableHandle=".drag-handle"
                margin={[16, 16]}
                compactType="vertical"
            >
{/* Executive Highlights: Alerts */}
            <WidgetWrapper key="showAlerts" id="showAlerts" isVisible={layout.showAlerts} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 flex flex-col shadow-sm" style={{ animationDelay: '0.33s' }}>
                    <h3 className="text-sm font-bold uppercase tracking-[0.2em] flex items-center gap-2 mb-5">
                        <AlertTriangle className="w-4 h-4 text-primary" /> Alerts & Anomalies
                    </h3>
                    <div className="flex flex-col gap-4 flex-1">
                        {data.operations.alerts.map(alert => (
                            <div key={alert.id} className={cn(
                                "p-3 sm:p-4 rounded-2xl border flex flex-wrap sm:flex-nowrap items-start gap-3 sm:gap-4",
                                alert.type === 'warning' ? "bg-amber-50/50 border-amber-200" :
                                alert.type === 'critical' ? "bg-red-50/50 border-red-200" :
                                "bg-emerald-50/50 border-emerald-200"
                            )}>
                                <div className={cn(
                                    "w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0",
                                    alert.type === 'warning' ? "bg-amber-100 text-amber-600" :
                                    alert.type === 'critical' ? "bg-red-100 text-red-600" :
                                    "bg-emerald-100 text-emerald-600"
                                )}>
                                    {alert.type === 'warning' ? <AlertTriangle className="w-5 h-5" /> :
                                     alert.type === 'critical' ? <AlertOctagon className="w-5 h-5" /> :
                                     <CheckCircle2 className="w-5 h-5" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h4 className={cn(
                                        "font-bold mb-1",
                                        alert.type === 'warning' ? "text-amber-900" :
                                        alert.type === 'critical' ? "text-red-900" :
                                        "text-emerald-900"
                                    )}>{alert.title}</h4>
                                    <p className="text-sm text-muted-foreground">{alert.message}</p>
                                </div>
                                <Button type="button" variant="outline" size="sm" className="mt-1 sm:ml-auto sm:mt-1 shrink-0">
                                    Review
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            </WidgetWrapper>

            {/* Financial Overview (KPI Cards) */}
            <WidgetWrapper key="showRevenueKpi" id="showRevenueKpi" isVisible={layout.showRevenueKpi} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-4 sm:p-6 relative overflow-hidden shadow-sm transition-shadow hover:shadow-md group" style={{ animationDelay: '0.05s' }}>
                    <div className="absolute opacity-[0.07] -right-6 -bottom-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                        <TrendingUp className="w-32 h-32" />
                    </div>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500/80 to-emerald-400/0" />
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Revenue · This Month
                    </p>
                    <p className="fluid-kpi font-bold font-mono text-primary tracking-tight num truncate">{formatCurrency(data.financials.totalRevenue)}</p>
                    <div className="mt-5 flex items-center justify-between">
                        {(() => {
                            const current = data.financials.totalRevenue;
                            const prev = data.financials.lastMonthRevenue;
                            let pct = 0;
                            if (prev > 0) {
                                pct = ((current - prev) / prev) * 100;
                            } else if (current > 0) {
                                pct = 100;
                            }
                            if (pct === 0) {
                                return (
                                    <div className="flex items-center gap-2 text-xs font-bold w-fit px-2.5 py-1 rounded-full text-zinc-600 bg-zinc-100">
                                        0.0%
                                    </div>
                                );
                            }
                            const isPositive = pct > 0;
                            return (
                                <div className={`flex items-center gap-1.5 text-xs font-bold w-fit px-2.5 py-1 rounded-full ${isPositive ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                                    {isPositive ? <TrendingUp className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3 rotate-90" />}
                                    {isPositive ? '+' : ''}{pct.toFixed(1)}%
                                </div>
                            );
                        })()}
                        <p className="text-xs font-medium text-muted-foreground tabular-nums">vs {formatCurrency(data.financials.lastMonthRevenue)}</p>
                    </div>
                </div>
            </WidgetWrapper>

            <WidgetWrapper key="showBillsKpi" id="showBillsKpi" isVisible={layout.showBillsKpi} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-4 sm:p-6 relative overflow-hidden shadow-sm transition-shadow hover:shadow-md group" style={{ animationDelay: '0.12s' }}>
                    <div className="absolute opacity-[0.07] -right-6 -bottom-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                        <Wallet className="w-32 h-32" />
                    </div>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500/80 to-amber-400/0" />
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500" /> Bills · This Month
                    </p>
                    <p className="fluid-kpi font-bold font-mono text-primary tracking-tight num truncate">{formatCurrency(data.financials.totalBills)}</p>
                    <div className="mt-5 flex items-center justify-between">
                        {(() => {
                            const current = data.financials.totalBills;
                            const prev = data.financials.lastMonthBills;
                            let pct = 0;
                            if (prev > 0) {
                                pct = ((current - prev) / prev) * 100;
                            } else if (current > 0) {
                                pct = 100;
                            }
                            if (pct === 0) {
                                return (
                                    <div className="flex items-center gap-2 text-xs font-bold w-fit px-2.5 py-1 rounded-full text-zinc-600 bg-zinc-100">
                                        0.0%
                                    </div>
                                );
                            }
                            const isPositive = pct > 0;
                            return (
                                <div className={`flex items-center gap-1.5 text-xs font-bold w-fit px-2.5 py-1 rounded-full ${!isPositive ? 'text-green-600 bg-green-50' : 'text-orange-600 bg-orange-50'}`}>
                                    {isPositive ? <TrendingUp className="w-3 h-3 rotate-180" /> : <ArrowUpRight className="w-3 h-3" />}
                                    {isPositive ? '+' : ''}{pct.toFixed(1)}%
                                </div>
                            );
                        })()}
                        <p className="text-xs font-medium text-muted-foreground tabular-nums">vs {formatCurrency(data.financials.lastMonthBills)}</p>
                    </div>
                </div>
            </WidgetWrapper>

            <WidgetWrapper key="showPendingInvoicesKpi" id="showPendingInvoicesKpi" isVisible={layout.showPendingInvoicesKpi} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-4 sm:p-6 relative overflow-hidden shadow-sm transition-shadow hover:shadow-md group" style={{ animationDelay: '0.19s' }}>
                    <div className="absolute opacity-[0.07] -right-6 -bottom-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                        <AlertCircle className="w-32 h-32" />
                    </div>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500/80 to-rose-400/0" />
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Pending Invoices
                    </p>
                    <p className="fluid-kpi font-bold font-mono text-primary tracking-tight num truncate">{formatCurrency(data.aging.receivables)}</p>
                    <p className="text-xs font-medium text-muted-foreground mt-5">Awaiting customer payment</p>
                </div>
            </WidgetWrapper>

            <WidgetWrapper key="showPendingBillsKpi" id="showPendingBillsKpi" isVisible={layout.showPendingBillsKpi} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-4 sm:p-6 relative overflow-hidden shadow-sm transition-shadow hover:shadow-md group" style={{ animationDelay: '0.26s' }}>
                    <div className="absolute opacity-[0.07] -right-6 -bottom-6 text-orange-500 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                        <AlertCircle className="w-32 h-32" />
                    </div>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500/80 to-orange-400/0" />
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Pending Bills
                    </p>
                    <p className="fluid-kpi font-bold font-mono text-primary tracking-tight num truncate">{formatCurrency(data.aging.payables)}</p>
                    <p className="text-xs font-medium text-muted-foreground mt-5">Awaiting your payment</p>
                </div>
            </WidgetWrapper>

            {/* Graphs Section */}
            <WidgetWrapper key="showPnLGraph" id="showPnLGraph" isVisible={layout.showPnLGraph} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 flex flex-col shadow-sm" style={{ animationDelay: '0.4s' }}>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm font-bold uppercase tracking-[0.2em] flex items-center gap-2">
                            <BarChart className="w-4 h-4 text-primary" /> Profit & Loss Overview
                        </h3>
                        <Badge variant="success" className="text-[10px] uppercase tracking-[0.12em]">
                            Operational Performance
                        </Badge>
                    </div>
                    <div className="flex-1 w-full min-h-[300px] relative">
                        <ResponsiveContainer width="100%" height="100%" minHeight={1} minWidth={1} debounce={50}>
                            <AreaChart data={data.analytics.plData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b', fontWeight: 'Bold' }} dy={10} />
                                <YAxis 
                                    tickFormatter={(val) => formatCompactCurrency(Number(val))}
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{ fontSize: 10, fill: '#64748b', fontWeight: 'Bold' }}
                                    dx={-10}
                                />
                                <RechartsTooltip 
                                    contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: '12px' }}
                                    formatter={(value: number) => formatCurrency(value)}
                                />
                                <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }} />
                                <Area type="monotone" dataKey="revenue" name="Revenue (Invoiced)" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                                <Area type="monotone" dataKey="expenses" name="Expenses (Accrued)" stroke="#ef4444" strokeWidth={3} fillOpacity={1} fill="url(#colorExpenses)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </WidgetWrapper>

            <WidgetWrapper key="showCashFlowGraph" id="showCashFlowGraph" isVisible={layout.showCashFlowGraph} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 flex flex-col shadow-sm" style={{ animationDelay: '0.47s' }}>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm font-bold uppercase tracking-[0.2em] flex items-center gap-2">
                            <Activity className="w-4 h-4 text-primary" /> Cash In/Out Maturity
                        </h3>
                        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em] text-primary">
                            Liquidity & Solvency
                        </Badge>
                    </div>
                    <div className="flex-1 w-full min-h-[300px] relative">
                        <ResponsiveContainer width="100%" height="100%" minHeight={1} minWidth={1} debounce={50}>
                            <BarChart data={data.analytics.cashFlowData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b', fontWeight: 'Bold' }} dy={10} />
                                <YAxis 
                                    tickFormatter={(val) => formatCompactCurrency(Number(val))}
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{ fontSize: 10, fill: '#64748b', fontWeight: 'Bold' }}
                                    dx={-10}
                                />
                                <RechartsTooltip 
                                    contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: '12px' }}
                                    formatter={(value: number) => formatCurrency(value)}
                                />
                                <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }} />
                                <Bar dataKey="inbound" name="Cash In (Receipts)" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
                                <Bar dataKey="outbound" name="Cash Out (Payments)" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={20} />
                                <Line type="monotone" dataKey="net" name="Net Cash Flow" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff' }} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-4 pt-4 border-t flex items-center justify-between">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Current Liquidity</p>
                            <p className="text-lg font-bold font-mono text-green-600">
                                {data.analytics.cashFlowData?.length > 0 
                                    ? formatCurrency(data.analytics.cashFlowData[data.analytics.cashFlowData.length - 1]?.net || 0)
                                    : formatCurrency(0)}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Burn Ratio</p>
                            <p className="text-lg font-bold font-mono text-primary">
                                {data.analytics.cashFlowData?.length > 0 && data.analytics.cashFlowData[data.analytics.cashFlowData.length - 1]?.inbound > 0
                                    ? ((data.analytics.cashFlowData[data.analytics.cashFlowData.length - 1]?.outbound / data.analytics.cashFlowData[data.analytics.cashFlowData.length - 1]?.inbound) * 100).toFixed(1)
                                    : '0.0'}%
                            </p>
                        </div>
                    </div>
                </div>
            </WidgetWrapper>

            <WidgetWrapper key="showExpenseBreakdown" id="showExpenseBreakdown" isVisible={layout.showExpenseBreakdown} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 flex flex-col shadow-sm" style={{ animationDelay: '0.54s' }}>
                    <h3 className="text-sm font-bold uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                        <PieChart className="w-4 h-4 text-primary" /> Expense Breakdown
                    </h3>
                    <div className="flex-1 w-full min-h-[300px] flex items-center justify-center relative">
                        <ResponsiveContainer width="100%" height="100%" minHeight={1} minWidth={1} debounce={50}>
                            <PieChart>
                                <Pie
                                    data={data.analytics.expenseBreakdown}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {data.analytics.expenseBreakdown.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <RechartsTooltip 
                                    formatter={(value: number) => formatCurrency(value)}
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="text-center">
                                <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Total Expenses</p>
                                <p className="text-xl font-bold font-mono">
                                    {formatCurrency(data.analytics.expenseBreakdown.reduce((acc, curr) => acc + curr.value, 0))}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                        {data.analytics.expenseBreakdown.map((item, i) => (
                            <div key={item.name} className="flex items-center gap-2 text-xs font-bold text-muted-foreground">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                                {item.name}
                            </div>
                        ))}
                    </div>
                </div>
            </WidgetWrapper>

            {/* Advanced Financials */}
            <WidgetWrapper key="showFinancialRatios" id="showFinancialRatios" isVisible={layout.showFinancialRatios} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 flex flex-col gap-4 shadow-sm" style={{ animationDelay: '0.61s' }}>
                    <h3 className="text-sm font-bold uppercase tracking-[0.2em] flex items-center gap-2">
                        <Percent className="w-4 h-4 text-primary" /> Key Financial Ratios
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-4 bg-muted/20 border rounded-2xl">
                            <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-1">Current Ratio</p>
                            <p className="text-2xl font-bold font-mono">{data.analytics.ratios.currentRatio}</p>
                            <p className="text-xs text-green-600 font-bold mt-1 inline-flex bg-green-50 px-2 py-0.5 rounded-full">&gt; 1.0 Is Good</p>
                        </div>
                        <div className="p-4 bg-muted/20 border rounded-2xl">
                            <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-1">Debt to Equity</p>
                            <p className="text-2xl font-bold font-mono">{data.analytics.ratios.debtToEquity}</p>
                            <p className="text-xs text-primary font-bold mt-1 inline-flex bg-primary/10 px-2 py-0.5 rounded-full">Healthy Leverage</p>
                        </div>
                        <div className="p-4 bg-muted/20 border rounded-2xl">
                            <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-1">Gross Margin</p>
                            <p className="text-2xl font-bold font-mono">{data.analytics.ratios.grossMargin}%</p>
                            <p className="text-xs text-muted-foreground font-medium mt-1">Direct Profitability</p>
                        </div>
                        <div className="p-4 bg-muted/20 border rounded-2xl">
                            <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-1">Net Margin</p>
                            <p className="text-2xl font-bold font-mono">{data.analytics.ratios.netMargin}%</p>
                            <p className="text-xs text-muted-foreground font-medium mt-1">Bottom Line Profit</p>
                        </div>
                        <div className="p-4 bg-muted/20 border rounded-2xl">
                            <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-1">DSO</p>
                            <p className="text-2xl font-bold font-mono">{data.analytics.ratios.dso} <span className="text-sm text-muted-foreground">Days</span></p>
                            <p className="text-xs text-muted-foreground font-medium mt-1">Sales Outstanding</p>
                        </div>
                        <div className="p-4 bg-muted/20 border rounded-2xl">
                            <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-1">DPO</p>
                            <p className="text-2xl font-bold font-mono">{data.analytics.ratios.dpo} <span className="text-sm text-muted-foreground">Days</span></p>
                            <p className="text-xs text-muted-foreground font-medium mt-1">Payables Outstanding</p>
                        </div>
                    </div>
                </div>
            </WidgetWrapper>

            <WidgetWrapper key="showEbitdaWaterfall" id="showEbitdaWaterfall" isVisible={layout.showEbitdaWaterfall} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 flex flex-col shadow-sm" style={{ animationDelay: '0.68s' }}>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm font-bold uppercase tracking-[0.2em] flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-primary" /> EBITDA Waterfall Bridge
                        </h3>
                        <Badge variant="secondary" className="text-[10px] uppercase tracking-[0.12em]">
                            Profitability Flow
                        </Badge>
                    </div>
                    <div className="flex-1 w-full min-h-[300px] relative">
                        <ResponsiveContainer width="100%" height="100%" minHeight={1} minWidth={1} debounce={50}>
                            <ComposedChart data={data.analytics.ebitdaWaterfall} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b', fontWeight: 'Bold' }} dy={10} />
                                <RechartsTooltip 
                                    formatter={(value: number) => formatCurrency(Math.abs(value))}
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    labelStyle={{ fontWeight: 'bold' }}
                                />
                                <Bar dataKey="value" name="Value" radius={[4, 4, 0, 0]}>
                                    {data.analytics.ebitdaWaterfall.map((entry, index) => (
                                        <Cell 
                                            key={`cell-${index}`} 
                                            fill={entry.isTotal ? '#3b82f6' : (entry.value >= 0 ? '#10b981' : '#ef4444')} 
                                        />
                                    ))}
                                </Bar>
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </WidgetWrapper>

            <WidgetWrapper key="showBudgetVariance" id="showBudgetVariance" isVisible={layout.showBudgetVariance} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 flex flex-col shadow-sm" style={{ animationDelay: '0.75s' }}>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm font-bold uppercase tracking-[0.2em] flex items-center gap-2">
                            <Target className="w-4 h-4 text-primary" /> Budget vs Actual Variance
                        </h3>
                    </div>
                    <div className="table-wrap">
                        <table className="w-full text-start border-collapse whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-muted">
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-start w-1/4">Category</th>
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right w-1/4">Actual</th>
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right w-1/4">Budget</th>
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right w-1/4">Variance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.analytics.budgetVariance.map((item, idx) => {
                                    const isFavorable = (item.category === 'Revenue') ? item.variance >= 0 : item.variance <= 0;
                                    return (
                                        <tr key={item.category} className="border-b border-muted/50 hover:bg-muted/30 transition-colors">
                                            <td className="py-4 px-2 font-bold text-sm">{item.category}</td>
                                            <td className="py-4 px-2 font-mono font-bold text-right text-sm">{formatCurrency(item.actual)}</td>
                                            <td className="py-4 px-2 font-mono font-bold text-right text-sm text-muted-foreground">{formatCurrency(item.budget)}</td>
                                            <td className="py-4 px-2 font-mono font-bold text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <span className={cn("text-sm", isFavorable ? "text-green-600" : "text-red-500")}>
                                                        {item.variance > 0 ? '+' : ''}{formatCurrency(item.variance)}
                                                    </span>
                                                    <span className={cn(
                                                        "text-[10px] px-2 py-0.5 rounded-full",
                                                        isFavorable ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                                                    )}>
                                                        {item.variancePct > 0 ? '+' : ''}{item.variancePct}%
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </WidgetWrapper>

            {/* Top Summaries Section */}
            <WidgetWrapper key="showTopCustomers" id="showTopCustomers" isVisible={layout.showTopCustomers} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 shadow-sm" style={{ animationDelay: '0.82s' }}>
                    <h3 className="text-sm font-bold uppercase tracking-[0.2em] flex items-center gap-2 mb-6">
                        <Star className="w-4 h-4 text-amber-500" /> Top Customers
                    </h3>
                    <div className="table-wrap">
                        <table className="w-full text-start border-collapse whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-muted">
                                    <th className="py-2 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-start">Customer</th>
                                    <th className="py-2 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-end">Value</th>
                                    <th className="py-2 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-end">Trend</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.analytics.topCustomers.map((customer, idx) => (
                                    <tr key={idx} className="border-b border-muted/50 hover:bg-muted/30 transition-colors">
                                        <td className="py-3 px-2 font-bold text-sm flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                                                {customer.name.substring(0, 2).toUpperCase()}
                                            </div>
                                            {customer.name}
                                        </td>
                                        <td className="py-3 px-2 font-mono font-bold text-right text-sm">{formatCurrency(customer.value)}</td>
                                        <td className="py-3 px-2 font-mono font-bold text-right">
                                            <span className={cn(
                                                "text-[10px] px-2 py-0.5 rounded-full inline-block",
                                                customer.trend.startsWith('+') ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                                            )}>
                                                {customer.trend}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </WidgetWrapper>
            
            <WidgetWrapper key="showTopExpenses" id="showTopExpenses" isVisible={layout.showTopExpenses} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 shadow-sm" style={{ animationDelay: '0.89s' }}>
                    <h3 className="text-sm font-bold uppercase tracking-[0.2em] flex items-center gap-2 mb-6">
                        <TrendingUp className="w-4 h-4 text-red-500" /> Top Expense Categories
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-muted">
                                    <th className="py-2 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Category</th>
                                    <th className="py-2 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">Amount</th>
                                    <th className="py-2 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">% of Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...data.analytics.expenseBreakdown]
                                    .sort((a, b) => b.value - a.value)
                                    .slice(0, 5)
                                    .map((expense, idx) => {
                                        const totalExpenses = data.analytics.expenseBreakdown.reduce((acc, curr) => acc + curr.value, 0);
                                        const percentage = totalExpenses > 0 ? ((expense.value / totalExpenses) * 100).toFixed(1) : '0.0';
                                        return (
                                            <tr key={idx} className="border-b border-muted/50 hover:bg-muted/30 transition-colors">
                                                <td className="py-3 px-2 font-bold text-sm flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-xl bg-red-50 text-red-500 flex items-center justify-center font-bold">
                                                        #{idx + 1}
                                                    </div>
                                                    {expense.name}
                                                </td>
                                                <td className="py-3 px-2 font-mono font-bold text-right text-sm">{formatCurrency(expense.value)}</td>
                                                <td className="py-3 px-2 font-mono font-bold text-right">
                                                    <div className="flex flex-col items-end gap-1">
                                                        <span className="text-xs text-muted-foreground">{percentage}%</span>
                                                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                                            <div 
                                                                className="h-full bg-red-400 rounded-full" 
                                                                style={{ width: `${percentage}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </WidgetWrapper>

            {/* Pending Payments Section */}
            <WidgetWrapper key="showPendingInvoicesList" id="showPendingInvoicesList" isVisible={layout.showPendingInvoicesList} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 shadow-sm" style={{ animationDelay: '0.96s' }}>
                    <h3 className="text-sm font-bold uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-500" /> Pending Invoices
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-muted">
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Contact Name</th>
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Creation Date</th>
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Due Date</th>
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.aging.pendingInvoicesList.map((invoice, idx) => (
                                    <tr key={idx} className="border-b border-muted/50 hover:bg-muted/30 transition-colors">
                                        <td className="py-3 px-2 text-sm font-bold">{invoice.contactName}</td>
                                        <td className="py-3 px-2 text-sm text-muted-foreground font-mono">{invoice.createdAt}</td>
                                        <td className="py-3 px-2 text-sm text-red-500 font-bold font-mono">{invoice.dueDate}</td>
                                        <td className="py-3 px-2 text-sm font-bold font-mono text-right">{formatCurrency(invoice.amount)}</td>
                                    </tr>
                                ))}
                                {data.aging.pendingInvoicesList.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="py-6 text-center text-sm text-muted-foreground italic">No pending invoices</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </WidgetWrapper>

            <WidgetWrapper key="showPendingBillsList" id="showPendingBillsList" isVisible={layout.showPendingBillsList} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 shadow-sm" style={{ animationDelay: '1.03s' }}>
                    <h3 className="text-sm font-bold uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-orange-500" /> Pending Bills (To Pay Suppliers)
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-muted">
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Supplier Name</th>
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Creation Date</th>
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Due Date</th>
                                    <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.aging.pendingBillsList.map((bill, idx) => (
                                    <tr key={idx} className="border-b border-muted/50 hover:bg-muted/30 transition-colors">
                                        <td className="py-3 px-2 text-sm font-bold">{bill.supplierName}</td>
                                        <td className="py-3 px-2 text-sm text-muted-foreground font-mono">{bill.createdAt}</td>
                                        <td className="py-3 px-2 text-sm text-orange-500 font-bold font-mono">{bill.dueDate}</td>
                                        <td className="py-3 px-2 text-sm font-bold font-mono text-right">{formatCurrency(bill.amount)}</td>
                                    </tr>
                                ))}
                                {data.aging.pendingBillsList.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="py-6 text-center text-sm text-muted-foreground italic">No pending bills</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </WidgetWrapper>

            {/* Operational Tracking */}
            <WidgetWrapper key="showAccountWatchlist" id="showAccountWatchlist" isVisible={layout.showAccountWatchlist} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 shadow-sm" style={{ animationDelay: '1.1s' }}>
                    <div className="flex items-center justify-between mb-6">
                            <h3 className="text-sm font-bold uppercase tracking-[0.2em] flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-primary" /> Account Watchlist
                            </h3>
                            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em] text-primary">
                                Financial Health Monitor
                            </Badge>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse whitespace-nowrap">
                                <thead>
                                    <tr className="border-b border-muted">
                                        <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Account</th>
                                        <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-center">Code</th>
                                        <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right border-x border-muted/30 bg-muted/10">This Month</th>
                                        <th className="py-3 px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">Year to Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {watchlistAccounts.map((acc) => {
                                        const netChange = monthlyBalances[acc.id] || 0;
                                        // Effective movement based on nature:
                                        // Debit nature: Positive impact = Debit - Credit (netChange)
                                        // Credit nature: Positive impact = Credit - Debit (-netChange)
                                        const movement = acc.nature === 'Debit' ? netChange : -netChange;
                                        
                                        return (
                                            <tr key={acc.id} className="border-b border-muted/50 hover:bg-muted/30 transition-colors group">
                                                <td className="py-4 px-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className={cn(
                                                            "w-1.5 h-1.5 rounded-full",
                                                            acc.nature === 'Debit' ? "bg-blue-500" : "bg-green-500"
                                                        )} />
                                                        <span className="text-sm font-bold">{acc.name}</span>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-2 text-center text-xs font-mono text-muted-foreground group-hover:text-primary transition-colors">
                                                    {acc.code}
                                                </td>
                                                <td className={cn(
                                                    "py-4 px-4 text-sm font-bold font-mono text-right border-x border-muted/30 bg-muted/5",
                                                    movement > 0 ? "text-green-600" : movement < 0 ? "text-red-500" : "text-muted-foreground"
                                                )}>
                                                    <div className="flex items-center justify-end gap-1">
                                                        {movement > 0 ? <ArrowUpRight className="w-3 h-3" /> : movement < 0 ? <ArrowDownLeft className="w-3 h-3" /> : null}
                                                        {formatCurrency(Math.abs(movement))}
                                                    </div>
                                                </td>
                                                <td className="py-4 px-2 text-sm font-bold font-mono text-right">
                                                    {formatCurrency(acc.ytd || 0)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {watchlistAccounts.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="py-12 text-center">
                                                <div className="flex flex-col items-center opacity-50 grayscale">
                                                    <BookOpen className="w-8 h-8 mb-2" />
                                                    <p className="text-sm italic">No accounts added to watchlist</p>
                                                    <p className="text-[10px] mt-1">Go to Chart of Accounts and check "Show on Dashboard Watchlist"</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
            </WidgetWrapper>

            <WidgetWrapper key="showRecentContacts" id="showRecentContacts" isVisible={layout.showRecentContacts} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 shadow-sm" style={{ animationDelay: '1.17s' }}>
                    <h3 className="text-sm font-bold uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                        <Users className="w-4 h-4 text-primary" /> Recent Contacts
                    </h3>
                    <div className="space-y-4">
                        {data.operations.recentContacts.map(contact => (
                            <div key={contact.id} className="flex items-center justify-between p-3 rounded-2xl bg-muted/30 hover:bg-muted transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                                        {(contact.name || '??').substring(0, 2).toUpperCase()}
                                    </div>
                                    <div>
                                        <p className="font-bold text-sm">{contact.name || 'Unknown'}</p>
                                        <p className="text-[10px] text-muted-foreground">{contact.type}</p>
                                    </div>
                                </div>
                                <span className="text-xs text-muted-foreground">{contact.time}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </WidgetWrapper>

            <WidgetWrapper key="showAttendance" id="showAttendance" isVisible={layout.showAttendance} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div className="h-full bg-card border border-border rounded-xl p-6 shadow-sm" style={{ animationDelay: '1.24s' }}>
                    <h3 className="text-sm font-bold uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-primary" /> Attendance Center
                    </h3>
                    <div className="flex flex-col items-center justify-center py-8 text-center bg-primary/5 rounded-2xl border border-primary/10">
                        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                            <span className="text-4xl font-black text-primary font-mono">{data.operations.activeSessions}</span>
                        </div>
                        <p className="font-bold text-lg">Active Sessions</p>
                        <p className="text-sm text-muted-foreground mt-1">Users currently checked in across organizations</p>
                        <div className="mt-6 flex flex-wrap justify-center gap-2 px-4">
                            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">HQ: 18</span>
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">Remote: 24</span>
                        </div>
                    </div>
                </div>
            </WidgetWrapper>

            <WidgetWrapper key="showTasks" id="showTasks" isVisible={layout.showTasks} isEditingLayout={isEditingLayout} toggleLayoutOption={toggleLayoutOption}>
                <div onClick={() => onNavigate?.('tasks_mops')} className="h-full bg-card border border-border rounded-xl p-6 shadow-sm cursor-pointer hover:border-primary/50 group" style={{ animationDelay: '1.31s' }}>
                    <h3 className="text-sm font-bold uppercase tracking-[0.2em] mb-6 flex items-center justify-between">
                        <span className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-primary" /> Task Management
                        </span>
                        <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 rounded-2xl border bg-orange-50/50 border-orange-100 hover:bg-orange-100 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-2 h-2 rounded-full bg-orange-500" />
                                <span className="font-bold text-sm">Pending</span>
                            </div>
                            <span className="font-bold">{data.operations.tasks.Pending}</span>
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-2xl border bg-blue-50/50 border-blue-100 hover:bg-blue-100 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                                <span className="font-bold text-sm">In Progress</span>
                            </div>
                            <span className="font-bold">{data.operations.tasks['In Progress']}</span>
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-2xl border bg-green-50/50 border-green-100 hover:bg-green-100 transition-colors">
                            <div className="flex items-center gap-3">
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                <span className="font-bold text-sm">Completed</span>
                            </div>
                            <span className="font-bold">{data.operations.tasks.Completed}</span>
                        </div>
                    </div>
                </div>
            </WidgetWrapper>
        </ResponsiveGridLayout>
        </div>

    );
};

export default ExecutiveDashboard;
